import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash';
import { IAlbum } from '@/data/models/album';
import { IPrice } from '@/data/models/salesTerritory';
import useGetAllowedSalesTeamsQuery from './useGetAllowedSalesTeamsQuery';
import useGetSalesTeamsQuery from './useGetSalesTeamsQuery';
import { IChangeRequest } from '@/data/models/changeRequest';
import useGetAllowedCRSalesTeamsQuery from '@/hooks/useGetAllowedCRSalesTeamsQuery';
import { getSaveErrorMessage } from '@/utils/saveErrorMessages';

export interface PricingData {
  salesTeamId: number;
  price: number | null;
  isPrimary: boolean;
  sequence: number | null;
  isCustomPrice?: boolean;
}

export interface PriceSelection {
  price: number | null;
  sequence: number | null;
  isCustomPrice?: boolean;
}

/**
 * Helper function to build the options array and selected value for a pricing dropdown.
 * Ensures that duplicate prices can be uniquely identified by their sequence ID and safely
 * appends custom prices not found in the standard tiers.
 */
export function buildPriceDropdownData(
  prices: IPrice[] | undefined,
  currentPrice: number | null | undefined,
  currentSequence: number | null | undefined,
  sortBy: 'price' | 'sequence'
) {
  // Create a shallow copy to prevent mutating the original sales team price list
  const priceOptions: IPrice[] = prices ? [...prices] : [];
  let seq = currentSequence;

  if (currentPrice != null) {
    // If a price is set but the sequence is missing, attempt to find the matching sequence ID
    if (seq == null) {
      const match = priceOptions.find((p) => p.price === currentPrice);
      if (match) seq = match.sequence;
    }

    // Check if the current price and sequence combination already exists in the standard options
    const currentOptionExists = priceOptions.some(
      (p) => p.sequence === seq && p.price === currentPrice
    );

    // If the selected price/sequence is not in the standard list (e.g., custom price), add it
    if (!currentOptionExists) {
      const sequenceInUse = priceOptions.some((p) => p.sequence === seq);
      // Generate a new, unique sequence ID for the custom price option to avoid collisions
      if (seq == null || sequenceInUse) {
        const maxSeq =
          priceOptions.length > 0
            ? Math.max(...priceOptions.map((p) => p.sequence))
            : -1;
        seq = maxSeq + 1;
      }
      priceOptions.push({ sequence: seq!, price: currentPrice });
    }
  }

  // Sort the options based on the requested criteria
  priceOptions.sort((a, b) =>
    sortBy === 'price' ? a.price - b.price : a.sequence - b.sequence
  );

  // Determine the HTML select value, using the sequence as the unique identifier
  const selectValue = seq != null ? String(seq) : '';
  return { priceOptions, selectValue };
}

/**
 * Custom keyboard navigation handler for select dropdowns.
 * Bypasses default HTML select behavior to correctly navigate through identical price values using sequence numbers.
 */
export const handleSelectKeyDown = (
  e: React.KeyboardEvent<HTMLSelectElement>,
  onChangeHandler: (value: string) => void
) => {
  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
    e.preventDefault();
    const select = e.currentTarget;

    let newIndex = select.selectedIndex;
    if (e.key === 'ArrowUp') {
      newIndex = Math.max(0, newIndex - 1);
    } else if (e.key === 'ArrowDown') {
      newIndex = Math.min(select.options.length - 1, newIndex + 1);
    }

    if (newIndex !== select.selectedIndex) {
      select.selectedIndex = newIndex;
      onChangeHandler(select.options[newIndex].value);
    }
  }
};

/**
 * Custom hook managing the state, data fetching, and business logic for the Product Pricing modal.
 */
export const usePricingModal = (
  album: IAlbum,
  togglePricingModal: () => void,
  updateProductPricing: (
    albumId: number,
    pricing: PricingData[]
  ) => Promise<void>,
  changeRequest: IChangeRequest | undefined
) => {
  const { t } = useTranslation();
  // List of sales teams where the product is explicitly allowed to be sold
  const { data: allowedSalesTeams = [], isSuccess: allowedSalesTeamsLoaded } =
    changeRequest
      ? useGetAllowedCRSalesTeamsQuery(album.id, changeRequest.id)
      : useGetAllowedSalesTeamsQuery(album.id);
  // Master list of all available sales teams globally
  const { data: allSalesTeams = [] } = useGetSalesTeamsQuery();
  // Map linking a sales team ID to its currently selected price and sequence tier
  const [pricingSelections, setPricingSelections] = useState<
    Map<number, PriceSelection>
  >(new Map());
  // ID of the sales team currently designated as the "primary" one
  const [primarySalesTeamId, setPrimarySalesTeamId] = useState<number | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCancelChangesModal, setShowCancelChangesModal] = useState(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);
  const initialPricingSelectionsRef = useRef<Map<number, PriceSelection>>(
    new Map()
  );
  const initialPrimarySalesTeamIdRef = useRef<number | null>(null);

  useEffect(() => {
    // Initialize pricing selections from album data
    const initialSelections = new Map<number, PriceSelection>();
    if (album.albumSalesTeams) {
      let currentPrimaryId: number | null = null;
      let currentPrimarySeq: number | null = null;
      album.albumSalesTeams.forEach((t) => {
        if (t.isPrimary) {
          currentPrimaryId = t.salesTeamId;
          currentPrimarySeq = t.sequence != null ? Number(t.sequence) : null;
        }
      });
      album.albumSalesTeams.forEach((t) => {
        initialSelections.set(t.salesTeamId, {
          price: t.price != null ? Number(t.price) : null,
          sequence: t.sequence != null ? Number(t.sequence) : null,
          isCustomPrice:
            t.isCustomPrice ??
            (t.isPrimary
              ? false
              : (t.sequence ?? null) !== (currentPrimarySeq ?? null)),
        });
      });
      setPrimarySalesTeamId(currentPrimaryId);
      initialPrimarySalesTeamIdRef.current = currentPrimaryId;
    }
    initialPricingSelectionsRef.current = new Map(initialSelections);
    setPricingSelections(initialSelections);
  }, [album.albumSalesTeams]);

  useEffect(() => {
    if (!allowedSalesTeamsLoaded || primarySalesTeamId == null) return;

    const primarySalesTeamIsAllowed = allowedSalesTeams.some(
      (salesTeam) => salesTeam.id === primarySalesTeamId
    );

    if (!primarySalesTeamIsAllowed) {
      setPrimarySalesTeamId(null);
      initialPrimarySalesTeamIdRef.current = null;
    }
  }, [allowedSalesTeams, allowedSalesTeamsLoaded, primarySalesTeamId]);

  // Alphabetically sort the allowed sales teams for display in the modal
  const sortedAllowedSalesTeams = useMemo(() => {
    return [...allowedSalesTeams].sort((a, b) =>
      t(a.salesTeamKey).localeCompare(t(b.salesTeamKey))
    );
  }, [allowedSalesTeams, t]);

  // Determine sales teams that are NOT explicitly allowed (i.e., inactive/restricted)
  const inactiveSalesTeams = useMemo(() => {
    const allowedIds = new Set(allowedSalesTeams.map((t) => t.id));
    return allSalesTeams
      .filter((t) => !allowedIds.has(t.id))
      .sort((a, b) => t(a.salesTeamKey).localeCompare(t(b.salesTeamKey)));
  }, [allSalesTeams, allowedSalesTeams, t]);

  // Get the full details of the currently selected primary sales team
  const primarySalesTeam = useMemo(() => {
    return sortedAllowedSalesTeams.find((t) => t.id === primarySalesTeamId);
  }, [sortedAllowedSalesTeams, primarySalesTeamId]);

  // Update the price and sequence for a specific non-primary sales team
  const handlePriceChange = useCallback(
    (salesTeamId: number, val: string) => {
      let seq: number | null = null;
      let newPrice: number | null = null;

      if (val) {
        const sequence = parseInt(val, 10);
        if (!isNaN(sequence)) {
          seq = sequence;
          const salesTeam = allowedSalesTeams.find((t) => t.id === salesTeamId);
          const priceObj = salesTeam?.prices?.find(
            (p) => p.sequence === sequence
          );
          newPrice = priceObj?.price ?? null;
        } else {
          newPrice = parseFloat(val);
        }
      }

      setPricingSelections((prev) => {
        const newMap = new Map(prev);
        const primarySelection =
          primarySalesTeamId != null && primarySalesTeam
            ? prev.get(primarySalesTeamId)
            : null;
        const primarySequence = primarySelection?.sequence ?? null;
        const isCustomPrice =
          newPrice != null &&
          primarySequence != null &&
          (seq == null || Number(seq) !== Number(primarySequence));
        newMap.set(salesTeamId, {
          price: newPrice,
          sequence: seq,
          isCustomPrice,
        });
        return newMap;
      });
    },
    [allowedSalesTeams, primarySalesTeam, primarySalesTeamId]
  );

  // Helper to cascade a selected sequence tier to all sales teams
  const applyCascadingUpdates = useCallback(
    (
      primaryId: number,
      sequenceId: number | null,
      primaryPrice: number | null
    ) => {
      const updates = new Map<number, PriceSelection>();

      // Always update the primary sales team's selection
      updates.set(primaryId, {
        price: primaryPrice,
        sequence: sequenceId,
        isCustomPrice: false,
      });

      // If a standard tier (sequence) is selected, calculate the cascaded updates for all other sales teams
      if (sequenceId != null) {
        sortedAllowedSalesTeams.forEach((t) => {
          if (t.id !== primaryId) {
            const matchingPrice = t.prices?.find(
              (p: IPrice) => p.sequence === sequenceId
            );
            if (matchingPrice) {
              updates.set(t.id, {
                price: matchingPrice.price,
                sequence: matchingPrice.sequence,
                isCustomPrice: false,
              });
            }
          }
        });
      }

      // Batch update the selections map to avoid multiple re-renders
      setPricingSelections((prev) => {
        const next = new Map(prev);
        updates.forEach((val, key) => next.set(key, val));

        // Re-evaluate isCustomPrice for ALL non-primary sales teams because the primary sequence changed
        next.forEach((selection, id) => {
          if (id !== primaryId) {
            const isCustomPrice =
              selection.price != null &&
              sequenceId != null &&
              (selection.sequence == null ||
                Number(selection.sequence) !== Number(sequenceId));
            if (selection.isCustomPrice !== isCustomPrice) {
              next.set(id, { ...selection, isCustomPrice });
            }
          }
        });

        return next;
      });
    },
    [sortedAllowedSalesTeams]
  );

  // Handle changing the primary sales team.
  // Triggers a cascade to apply the newly selected primary sales team's tier/sequence to all other allowed sales teams.
  const onPrimarySalesTeamChange = useCallback(
    (selectedId: string) => {
      if (!selectedId) {
        setPrimarySalesTeamId(null);
        return;
      }

      const selectedT = sortedAllowedSalesTeams.find(
        (t) => String(t.id) === selectedId
      );
      if (selectedT) {
        // Update the primary sales team ID state.
        setPrimarySalesTeamId(selectedT.id);

        // Retrieve the current price and sequence tier already set for this newly selected primary sales team.
        const selection = pricingSelections.get(selectedT.id);
        const currentPrice = selection?.price ?? null;
        let selectedSeq = selection?.sequence ?? null;

        // If a price exists but the sequence is missing (e.g., from initial load), attempt to find the matching sequence ID.
        if (selectedSeq == null) {
          const match = selectedT.prices?.find((p) => p.price === currentPrice);
          if (match) selectedSeq = match.sequence;
        }

        // Cascade this tier to all other sales teams
        applyCascadingUpdates(selectedT.id, selectedSeq, currentPrice);
      }
    },
    [sortedAllowedSalesTeams, pricingSelections, applyCascadingUpdates]
  );

  // Handle changing the price of the primary sales team.
  // Triggers a cascade to update the prices of all other allowed sales teams to match the newly selected sequence tier.
  const onPrimaryPriceChange = useCallback(
    (val: string) => {
      if (!primarySalesTeam) return;

      let seq: number | null = null;
      let newPrice: number | null = null;

      if (val) {
        // Parse the incoming value. Dropdowns primarily pass the 'sequence' ID to handle duplicate price tiers.
        const sequence = parseInt(val, 10);
        if (!isNaN(sequence)) {
          seq = sequence;
          // Find the corresponding price object in the primary sales team using the sequence ID.
          const priceObj = primarySalesTeam.prices?.find(
            (p) => p.sequence === sequence
          );
          newPrice = priceObj?.price ?? null;
        } else {
          // Fallback: If it's not a valid sequence integer, treat the value as a raw floating-point custom price.
          newPrice = parseFloat(val);
        }
      }

      applyCascadingUpdates(primarySalesTeam.id, seq, newPrice);
    },
    [primarySalesTeam, applyCascadingUpdates]
  );

  // Format the pricing data payload and invoke the save callback
  const handleSubmit = useCallback(async () => {
    if (!album?.id) return;
    setIsSubmitting(true);
    setSaveErrorMessage(null);
    const pricingData: PricingData[] = [];

    const primarySelection =
      primarySalesTeamId != null
        ? pricingSelections.get(primarySalesTeamId)
        : null;
    const primarySequence = primarySelection?.sequence ?? null;

    pricingSelections.forEach((selection, id) => {
      // Blank PPDs are represented by omitting the sales team price row.
      if (selection.price != null) {
        pricingData.push({
          salesTeamId: id,
          price: selection.price,
          sequence: selection.sequence,
          isCustomPrice:
            id === primarySalesTeamId || primarySequence == null
              ? false
              : selection.sequence == null ||
                Number(selection.sequence) !== Number(primarySequence),
          isPrimary: id === primarySalesTeamId,
        });
      }
    });

    try {
      await updateProductPricing(Number(album?.id), pricingData);
      togglePricingModal();
    } catch (error) {
      setSaveErrorMessage(getSaveErrorMessage(error, !!changeRequest));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    album?.id,
    changeRequest,
    pricingSelections,
    primarySalesTeamId,
    updateProductPricing,
    togglePricingModal,
  ]);

  const closeWithoutSaving = useCallback(() => {
    setShowCancelChangesModal(false);
    togglePricingModal();
  }, [togglePricingModal]);

  const onCancel = useCallback(() => {
    const hasChanges =
      primarySalesTeamId !== initialPrimarySalesTeamIdRef.current ||
      !isEqual(pricingSelections, initialPricingSelectionsRef.current);

    if (hasChanges) {
      setShowCancelChangesModal(true);
    } else {
      closeWithoutSaving();
    }
  }, [closeWithoutSaving, pricingSelections, primarySalesTeamId]);

  return {
    allowedSalesTeams,
    sortedAllowedSalesTeams,
    inactiveSalesTeams,
    primarySalesTeam,
    pricingSelections,
    handlePriceChange,
    primarySalesTeamId,
    onPrimarySalesTeamChange,
    onPrimaryPriceChange,
    handleSubmit,
    onCancel,
    closeWithoutSaving,
    isSubmitting,
    saveErrorMessage,
    setSaveErrorMessage,
    showCancelChangesModal,
    setShowCancelChangesModal,
    handleSelectKeyDown,
  };
};
